import axios from "axios";

const apiUrl = import.meta.env.VITE_API_URL;

export const transcribe = async (
  audioFile: File,
  options?: {
    signal?: AbortSignal;
    onProgress?: (progress: number, message: string) => void;
    onComplete?: (result: any) => void;
    onError?: (error: string) => void;
  },
) => {
  try {
    // Initial API call to start transcription
    const formData = new FormData();
    formData.append("file", audioFile);
    formData.append("size", "medium");

    const response = await fetch(`${apiUrl}/transcribe?size=medium`, {
      method: "POST",
      body: formData,
      signal: options?.signal,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Server error response:", errorText);
      throw new Error(`Failed to start transcription: ${errorText}`);
    }

    const data = await response.json().catch((error) => {
      console.error("JSON parsing error:", error);
      throw new Error("Failed to parse server response as JSON");
    });

    const { client_id } = data;

    // Use the working WebSocket URL structure
    const wsBaseUrl = apiUrl.replace("https://", "wss://");
    const wsUrl = `${wsBaseUrl}/ws?client_id=${client_id}`;

    const ws = new WebSocket(wsUrl);

    return new Promise((resolve, reject) => {
      const connectionTimeout = setTimeout(() => {
        if (ws.readyState !== WebSocket.OPEN) {
          ws.close();
          reject(new Error("WebSocket connection timeout"));
        }
      }, 500000);

      ws.onopen = () => {
        clearTimeout(connectionTimeout);
        // Send initial message to verify connection
        ws.send(JSON.stringify({ type: "connect", client_id }));
      };

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.type === "ping" || data.type === "pong") {
          return;
        } else if (data.type === "connect_ack") {
          return;
        } else {
          try {
            const data = JSON.parse(event.data);
            ws.send(JSON.stringify({ type: "ping" }));
            switch (data.state.status) {
              case "processing":
                console.log(
                  `Progress: ${data.state.progress}% - ${data.state.message}`,
                );
                options?.onProgress?.(data.state.progress, data.state.message);
                break;
              case "completed":
                options?.onComplete?.(data.state.result);
                ws.send(JSON.stringify({ type: "websocket.close" }));
                ws.close();
                resolve(data.state.result);
                break;
              case "error":
                console.error("Server reported error:", data.state.message);
                options?.onError?.(data.state.message);
                ws.close();
                reject(new Error(data.state.message));
                break;
            }
          } catch (error) {
            console.error("Error parsing message:", error, event.data);
          }
        }
      };

      ws.onerror = (event) => {
        console.error("WebSocket error:", event);
        clearTimeout(connectionTimeout);
        options?.onError?.("WebSocket connection error");
        reject(new Error("WebSocket connection error"));
      };

      ws.onclose = (event) => {
        console.log("WebSocket connection closed:", event.code, event.reason);
        clearTimeout(connectionTimeout);
      };
    });
  } catch (error) {
    console.error("Transcription error:", error);
    options?.onError?.(
      error instanceof Error ? error.message : "Unknown error",
    );
    throw error;
  }
};
